import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../auth/authContext.ts";
import { HipotesisNormativasCard } from "../../components/HipotesisNormativas.tsx";
import {
  apiConfigurada,
  crearAportacion,
  destinoDeAportacion,
  evaluacionesUltimas,
  hipotesisPorAportaciones,
  listarAportaciones,
  listarPersonas,
  listarProyectos,
  type AportacionRow,
  type EvaluacionMotor,
  type HipotesisNormativa,
  type PersonaRow,
  type ProyectoMin,
} from "../../lib/api.ts";
import { Chip, Fila } from "../../ui/componentes.tsx";
import { IconoCaptacion } from "../../ui/iconos.tsx";
import { nombrePersona } from "../../ui/persona.ts";
import { C, dinero, fecha, st } from "../../ui/theme.ts";

/**
 * CAPTACIÓN — rebanada 2 de la beta. Registra la recepción de recursos
 * (Art. 17 fr. V Bis) y muestra lo que el MOTOR REAL resolvió al insertar
 * (trigger → evaluar_aportacion → bitácora): fracción, doble base, acumulación
 * de 6 meses, prohibición Art. 32 y semáforo. La UI no calcula nada normativo:
 * lo lee. En modo demostración (sin backend) el semáforo queda en gris con nota.
 */

const TIPOS_APORTACION: [string, string][] = [
  ["recursos_propios", "Recursos propios"],
  ["socios", "Aportación de socio"],
  ["terceros", "Tercero / preventa"],
  ["prestamo_financiero", "Préstamo financiero"],
  ["prestamo_no_financiero", "Préstamo no financiero"],
  ["financiamiento_bursatil", "Financiamiento bursátil"],
];

const INSTRUMENTOS: [string, string][] = [
  ["8", "Transferencia interbancaria"],
  ["9", "Transferencia misma institución"],
  ["5", "Cheque nominativo"],
  ["1", "Efectivo"],
];

const CLASES_APORTACION: [string, string][] = [
  ["numerario", "Numerario"],
  ["especie", "Especie"],
];

const CLAVE_EFECTIVO = "1";
const OPACIDAD_DESHABILITADO = 0.6;
const ES_FINANCIAMIENTO = new Set(["prestamo_financiero", "prestamo_no_financiero", "financiamiento_bursatil"]);

function chipSemaforo(sem: string | undefined) {
  switch (sem) {
    case "verde":
      return <Chip tono="verde">● Sin obligación</Chip>;
    case "ambar":
      return <Chip tono="ambar">● Genera aviso</Chip>;
    case "rojo":
      return <Chip tono="rojo">● Prohibición Art. 32</Chip>;
    case "gris":
      return <Chip tono="gris">● Requiere revisión</Chip>;
    default:
      return <Chip tono="gris">Sin evaluación</Chip>;
  }
}

/** El veredicto del motor, en español de persona. */
function explicacion(e: EvaluacionMotor): { tono: "verde" | "ambar" | "rojo" | "gris"; texto: string } {
  if (e.prohibicion_efectivo) {
    return {
      tono: "rojo",
      texto:
        "El pago en efectivo supera el límite legal (Art. 32): el recurso NO puede aplicarse al proyecto. El registro queda como constancia del cumplimiento.",
    };
  }
  if (e.prohibicion_indeterminada) {
    return {
      tono: "gris",
      texto: "No se pudo determinar el límite de efectivo a la fecha del acto (hueco de catálogo). Revisión del REC requerida — el hueco es visible, no silencioso.",
    };
  }
  if (e.requiere_aviso) {
    const via = e.excede_individual ? "por sí sola" : "por la suma de 6 meses del cliente";
    const vence = e.vence_aviso ? ` Plazo del aviso: ${fecha(e.vence_aviso)}.` : "";
    return {
      tono: "ambar",
      texto: `Cruza el umbral de aviso ${via}: Argus generó el aviso y lo ligó al informe del mes.${vence}`,
    };
  }
  return {
    tono: "verde",
    texto: "Sin obligación de aviso. Queda registrada, conservada y sumando en la ventana de 6 meses del cliente.",
  };
}

export function CaptacionView() {
  const { session } = useAuth();
  const token = session?.accessToken ?? "";
  const tenantId = session?.tenantId ?? "";
  const demo = !apiConfigurada() || token === "";

  const [aportaciones, setAportaciones] = useState<AportacionRow[]>([]);
  const [evals, setEvals] = useState<Map<string, EvaluacionMotor>>(new Map());
  const [hipotesis, setHipotesis] = useState<Map<string, HipotesisNormativa[]>>(new Map());
  const [personas, setPersonas] = useState<PersonaRow[]>([]);
  const [proyectos, setProyectos] = useState<ProyectoMin[]>([]);
  const [selId, setSelId] = useState<string | null>(null);
  const [modo, setModo] = useState<"detalle" | "nueva">("detalle");
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(!demo);
  const [destino, setDestino] = useState<string | null>(null);

  const sel = aportaciones.find((a) => a.id === selId) ?? null;
  const evalSel = sel ? evals.get(sel.id) : undefined;

  const recargar = useCallback(async () => {
    if (demo) return;
    const [aps, per, pro] = await Promise.all([listarAportaciones(token), listarPersonas(token), listarProyectos(token)]);
    setAportaciones(aps);
    setPersonas(per);
    setProyectos(pro);
    const ids = aps.map((a) => a.id);
    const [ev, hn] = await Promise.all([evaluacionesUltimas(token, ids), hipotesisPorAportaciones(token, ids)]);
    setEvals(ev);
    setHipotesis(hn);
  }, [demo, token]);

  useEffect(() => {
    if (demo) {
      setAportaciones([]);
      return;
    }
    setCargando(true);
    recargar()
      .catch((e: unknown) => { setError(e instanceof Error ? e.message : "No se pudo cargar captación."); })
      .finally(() => { setCargando(false); });
  }, [demo, recargar]);

  useEffect(() => {
    setDestino(null);
    if (!sel || demo) return;
    destinoDeAportacion(token, sel.id)
      .then((filas) => {
        const inf = filas[0]?.aviso?.informe;
        if (inf) setDestino(`Ligada al informe de ${inf.mes_reportado} (estado: ${inf.estado})`);
      })
      .catch(() => { /* destino es informativo */ });
  }, [sel, demo, token]);

  return (
    <div className="argus-master-detail" style={{ display: "flex", flex: 1, minWidth: 0 }}>
      {/* Lista central */}
      <section className="argus-list-pane" style={{ width: 330, flexShrink: 0, borderRight: `1px solid ${C.line}`, display: "flex", flexDirection: "column", background: "#FBFCFB" }}>
        <div style={{ padding: "16px 16px 10px" }}>
          <h2 style={{ ...st.display, margin: 0, fontSize: 18 }}>Captación</h2>
          <p style={{ margin: "2px 0 0", fontSize: 12, color: C.muted }}>
            Recepción de recursos para el desarrollo (fr. V Bis). El motor evalúa cada registro.
          </p>
        </div>
        {demo && (
          <div style={{ margin: "8px 16px", padding: "6px 10px", borderRadius: 8, background: C.amberBg, color: C.amber, fontSize: 11.5, fontWeight: 600 }}>
            Modo demostración — el motor normativo corre en el backend; aquí sólo se ve el flujo.
          </div>
        )}
        <div style={{ flex: 1, overflow: "auto", padding: "6px 12px 8px" }}>
          {cargando && <p style={{ color: C.muted, fontSize: 13, padding: "0 6px" }}>Cargando…</p>}
          {!cargando && aportaciones.length === 0 && (
            <p style={{ color: C.muted, fontSize: 13, padding: "0 6px" }}>Sin recepciones registradas — captura la primera.</p>
          )}
          {aportaciones.map((a) => {
            const activa = selId === a.id && modo === "detalle";
            const ev = evals.get(a.id);
            return (
              <button
                key={a.id}
                onClick={() => { setSelId(a.id); setModo("detalle"); setError(null); }}
                style={{
                  width: "100%",
                  textAlign: "left",
                  background: "#fff",
                  border: `1px solid ${activa ? C.tiel : C.line}`,
                  boxShadow: activa ? `0 0 0 1px ${C.tiel}` : "none",
                  borderRadius: 12,
                  padding: "10px 12px",
                  marginBottom: 8,
                  cursor: "pointer",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: 13.5, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {nombrePersona(a.cliente_aportante)}
                  </span>
                  <span style={{ fontWeight: 700, fontSize: 13, color: C.ink, whiteSpace: "nowrap" }}>{dinero(a.monto_bruto)}</span>
                </div>
                <div style={{ fontSize: 12, color: C.muted, margin: "2px 0 6px" }}>
                  {a.proyecto?.nombre ?? "(sin proyecto)"} · {fecha(a.fecha_acto)}
                  {a.es_efectivo ? " · efectivo" : ""}
                </div>
                {chipSemaforo(ev?.semaforo)}
              </button>
            );
          })}
        </div>
        <div style={{ padding: 12, borderTop: `1px solid ${C.line}` }}>
          <button style={{ ...st.btnPrim, width: "100%" }} onClick={() => { setModo("nueva"); setSelId(null); setError(null); }}>
            ＋ Registrar recepción
          </button>
        </div>
      </section>

      {/* Detalle / captura */}
      <main className="argus-detail-pane" style={{ flex: 1, overflow: "auto", padding: "22px 26px", minWidth: 0 }}>
        {error && (
          <div style={{ maxWidth: 560, margin: "0 auto 12px", padding: "10px 14px", borderRadius: 10, background: C.redBg, color: C.red, fontSize: 13 }}>
            {error}
          </div>
        )}
        {modo === "nueva" ? (
          <FormRecepcion
            demo={demo}
            personas={personas}
            proyectos={proyectos}
            onCancelar={() => { setModo("detalle"); }}
            onCrear={async (datos) => {
              setError(null);
              try {
                if (demo) {
                  setError("En modo demostración el alta no corre el motor. Conecta el backend para capturar de verdad.");
                  return;
                }
                const fila = await crearAportacion(token, tenantId, datos);
                await recargar();
                setSelId(fila.id);
                setModo("detalle");
              } catch (e) {
                setError(e instanceof Error ? e.message : "No se pudo registrar la recepción.");
              }
            }}
          />
        ) : sel ? (
          <div style={{ maxWidth: 560, margin: "0 auto" }}>
            <h2 style={{ margin: "0 0 2px", fontSize: 20 }}>{dinero(sel.monto_bruto)}</h2>
            <p style={{ margin: "0 0 10px", color: C.muted, fontSize: 13 }}>
              {nombrePersona(sel.cliente_aportante)} · {sel.proyecto?.nombre ?? "(sin proyecto)"} · {fecha(sel.fecha_acto)}
            </p>
            <div style={{ marginBottom: 14 }}>{chipSemaforo(evalSel?.semaforo)}</div>

            {evalSel && (
              (() => {
                const ex = explicacion(evalSel);
                const fondo = { verde: C.greenBg, ambar: C.amberBg, rojo: C.redBg, gris: C.grayBg }[ex.tono];
                const color = { verde: C.green, ambar: C.amber, rojo: C.red, gris: C.muted }[ex.tono];
                return (
                  <div style={{ ...st.tarjeta, background: fondo, borderColor: fondo }}>
                    <p style={{ ...st.eyebrow, color }}>Lo que el motor resolvió</p>
                    <div style={{ fontSize: 13.5, color: C.ink }}>{ex.texto}</div>
                  </div>
                );
              })()
            )}

            <div style={st.tarjeta}>
              <p style={st.eyebrow}>Montos (doble base — nunca se colapsan)</p>
              <Fila k="Monto base (umbral de aviso, sin IVA)" v={dinero(sel.monto_base)} />
              <Fila k="Contribuciones / IVA" v={dinero(sel.monto_contribuciones)} />
              <Fila k="Monto bruto (límite de efectivo)" v={dinero(sel.monto_bruto)} />
              <Fila k="Instrumento" v={INSTRUMENTOS.find(([c]) => c === sel.instrumento_monetario)?.[1] ?? (sel.es_efectivo ? "Efectivo" : "—")} />
              <Fila k="Tipo de aportación" v={TIPOS_APORTACION.find(([c]) => c === sel.tipo_aportacion)?.[1] ?? "—"} />
              <Fila k="Forma de aportación" v={sel.aportacion_clase === "especie" ? "Especie" : "Numerario"} />
              {sel.aportacion_clase === "especie" && (
                <>
                  <Fila k="Bien aportado" v={sel.descripcion_bien ?? "—"} />
                  <Fila k="Monto estimado" v={dinero(sel.monto_estimado)} />
                </>
              )}
              {sel.es_preventa && <Fila k="Valor del inmueble (preventa)" v={dinero(sel.valor_inmueble_preventa)} />}
            </div>

            {evalSel && (
              <div style={st.tarjeta}>
                <p style={st.eyebrow}>Cálculo normativo (a la fecha del acto)</p>
                <Fila k="Fracción efectiva" v={evalSel.fraccion_efectiva ?? "—"} />
                <Fila k="Umbral de aviso (pesos, por catálogo)" v={dinero(evalSel.umbral_pesos ?? null)} />
                <Fila k="Base individual" v={dinero(evalSel.base_individual ?? null)} />
                <Fila k="Acumulado 6 meses del cliente" v={dinero(evalSel.acumulado_persona ?? null)} />
                <Fila k="¿Cruza umbral?" v={evalSel.excede_individual ? "Sí — individual" : evalSel.excede_acumulado ? "Sí — acumulado" : "No"} />
                {evalSel.estado_exigibilidad && <Fila k="Exigibilidad del formato" v={evalSel.estado_exigibilidad} />}
              </div>
            )}

            <HipotesisNormativasCard hipotesis={hipotesis.get(sel.id)} />

            {destino && (
              <div style={{ ...st.tarjeta, background: C.tielSoft, borderColor: "#C8E0DD" }}>
                <span style={{ fontSize: 13.5, color: C.tielDark, fontWeight: 600 }}>{destino}</span>
              </div>
            )}
          </div>
        ) : (
          <div style={{ maxWidth: 420, margin: "18vh auto 0", textAlign: "center", color: C.muted }}>
            <div style={{ marginBottom: 8, color: C.tielDark }}><IconoCaptacion size={34} /></div>
            <h3 style={{ margin: "0 0 6px", color: C.ink }}>Selecciona una recepción</h3>
            <p style={{ fontSize: 13.5, margin: 0 }}>
              Cada registro muestra lo que el motor resolvió: fracción, umbral por catálogo,
              acumulación de 6 meses, Art. 32 y a qué informe quedó ligado.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Alta de recepción
// ─────────────────────────────────────────────────────────────────────────────

function FormRecepcion({
  demo,
  personas,
  proyectos,
  onCrear,
  onCancelar,
}: {
  demo: boolean;
  personas: PersonaRow[];
  proyectos: ProyectoMin[];
  onCrear: (datos: Record<string, unknown>) => Promise<void>;
  onCancelar: () => void;
}) {
  const [personaId, setPersonaId] = useState("");
  const [proyectoId, setProyectoId] = useState("");
  const [tipo, setTipo] = useState("terceros");
  const [aportacionClase, setAportacionClase] = useState("numerario");
  const [fecha, setFecha] = useState("");
  const [base, setBase] = useState("");
  const [iva, setIva] = useState("");
  const [instrumento, setInstrumento] = useState("8");
  const [descripcionBien, setDescripcionBien] = useState("");
  const [montoEstimado, setMontoEstimado] = useState("");
  const [preventa, setPreventa] = useState(false);
  const [valorInmueble, setValorInmueble] = useState("");
  const [fin, setFin] = useState<Record<string, string>>({});
  const [guardando, setGuardando] = useState(false);

  const persona = personas.find((p) => p.id === personaId);
  const nBase = Number(base) || 0;
  const nIva = Number(iva) || 0;
  const bruto = nBase + nIva;
  const esEspecie = aportacionClase === "especie";
  const nMontoEstimado = Number(montoEstimado) || 0;
  const listo = personaId !== "" && proyectoId !== "" && fecha !== "" && nBase > 0 &&
    (!esEspecie || (descripcionBien.trim() !== "" && nMontoEstimado > 0));

  const finCampo = (k: string) => fin[k] ?? "";
  const setFinCampo = (k: string, v: string) => { setFin((prev) => ({ ...prev, [k]: v })); };

  const armar = (): Record<string, unknown> => {
    const d: Record<string, unknown> = {
      cliente_aportante_id: personaId,
      proyecto_id: proyectoId,
      tipo_aportacion: tipo,
      aportacion_clase: aportacionClase,
      fecha_acto: fecha,
      monto_base: nBase,
      monto_contribuciones: nIva,
      monto_bruto: bruto,
      es_efectivo: !esEspecie && instrumento === CLAVE_EFECTIVO,
      instrumento_monetario: esEspecie ? null : instrumento,
      // #38: moneda extranjera sigue bloqueada hasta tener conversión determinista.
      moneda: "1",
      descripcion_bien: esEspecie ? descripcionBien.trim() : null,
      monto_estimado: esEspecie ? nMontoEstimado : null,
      es_preventa: !esEspecie && preventa,
      valor_inmueble_preventa: !esEspecie && preventa && valorInmueble !== "" ? Number(valorInmueble) : null,
    };
    if (ES_FINANCIAMIENTO.has(tipo)) {
      if (finCampo("plazo") !== "") d["plazo_meses"] = Number(finCampo("plazo"));
      if (tipo === "prestamo_financiero") {
        if (finCampo("tipo_credito") !== "") d["tipo_credito"] = finCampo("tipo_credito");
        if (finCampo("tipo_institucion") !== "") d["tipo_institucion"] = finCampo("tipo_institucion");
      }
      if (tipo === "financiamiento_bursatil") {
        if (finCampo("monto_solicitado") !== "") d["monto_solicitado"] = Number(finCampo("monto_solicitado"));
        if (finCampo("fecha_emision") !== "") d["fecha_emision"] = finCampo("fecha_emision");
      }
    }
    return d;
  };

  return (
    <div style={{ maxWidth: 560, margin: "0 auto" }}>
      <button onClick={onCancelar} style={{ border: "none", background: "transparent", color: C.muted, fontSize: 13, cursor: "pointer", padding: 0, marginBottom: 8 }}>
        ‹ Cancelar
      </button>
      <h2 style={{ margin: "0 0 2px", fontSize: 20 }}>Registrar recepción de recursos</h2>
      <p style={{ margin: "0 0 14px", color: C.muted, fontSize: 13 }}>
        Captura el hecho; el motor clasifica la fracción, resuelve el umbral por catálogo a la
        fecha del acto, acumula 6 meses y aplica el Art. 32 — y te muestra el resultado.
      </p>

      <div style={st.tarjeta}>
        <p style={st.eyebrow}>¿Quién entrega y para qué proyecto?</p>
        <div style={{ display: "grid", gap: 10 }}>
          <div>
            <label style={st.label}>Persona (expediente ya capturado)</label>
            <select style={st.input} value={personaId} onChange={(e) => { setPersonaId(e.target.value); }}>
              <option value="">Selecciona…</option>
              {personas.map((p) => (
                <option key={p.id} value={p.id}>{nombrePersona(p)}</option>
              ))}
            </select>
            {persona && !persona.expediente_completo && (
              <div style={{ marginTop: 6, padding: "7px 10px", borderRadius: 8, background: C.amberBg, color: C.amber, fontSize: 12 }}>
                Expediente incompleto: puedes registrar, pero cumplimiento deberá completarlo — la identificación es obligatoria desde el primer peso.
              </div>
            )}
          </div>
          <div>
            <label style={st.label}>Proyecto</label>
            <select style={st.input} value={proyectoId} onChange={(e) => { setProyectoId(e.target.value); }}>
              <option value="">Selecciona…</option>
              {proyectos.map((p) => (
                <option key={p.id} value={p.id}>{p.nombre}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div style={st.tarjeta}>
        <p style={st.eyebrow}>La operación</p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div style={{ gridColumn: "span 2" }}>
            <label style={st.label}>Tipo de aportación (choice del aviso DIN)</label>
            <select style={st.input} value={tipo} onChange={(e) => { setTipo(e.target.value); }}>
              {TIPOS_APORTACION.map(([v, t]) => (
                <option key={v} value={v}>{t}</option>
              ))}
            </select>
          </div>
          <div style={{ gridColumn: "span 2" }}>
            <label style={st.label}>Forma de aportación</label>
            <div style={{ display: "flex", gap: 8 }}>
              {CLASES_APORTACION.map(([v, t]) => (
                <button
                  key={v}
                  onClick={() => { setAportacionClase(v); }}
                  style={{
                    ...st.btn,
                    borderColor: aportacionClase === v ? C.tiel : C.line,
                    background: aportacionClase === v ? C.tielSoft : "#fff",
                    color: aportacionClase === v ? C.tielDark : C.ink,
                  }}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label style={st.label}>Fecha de recepción efectiva</label>
            <input style={st.input} type="date" value={fecha} onChange={(e) => { setFecha(e.target.value); }} />
          </div>
          {!esEspecie && (
            <div>
              <label style={st.label}>Instrumento</label>
              <select style={st.input} value={instrumento} onChange={(e) => { setInstrumento(e.target.value); }}>
                {INSTRUMENTOS.map(([v, t]) => (
                  <option key={v} value={v}>{t}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label style={st.label}>Monto sin IVA (base de umbral)</label>
            <input style={st.input} inputMode="numeric" placeholder="0" value={base} onChange={(e) => { setBase(e.target.value.replace(/[^0-9.]/g, "")); }} />
          </div>
          <div>
            <label style={st.label}>IVA / contribuciones</label>
            <input style={st.input} inputMode="numeric" placeholder="0" value={iva} onChange={(e) => { setIva(e.target.value.replace(/[^0-9.]/g, "")); }} />
          </div>
          {esEspecie && (
            <>
              <div style={{ gridColumn: "span 2" }}>
                <label style={st.label}>Descripción del bien</label>
                <input style={st.input} value={descripcionBien} onChange={(e) => { setDescripcionBien(e.target.value); }} />
              </div>
              <div style={{ gridColumn: "span 2" }}>
                <label style={st.label}>Monto estimado del bien</label>
                <input style={st.input} inputMode="numeric" placeholder="0" value={montoEstimado} onChange={(e) => { setMontoEstimado(e.target.value.replace(/[^0-9.]/g, "")); }} />
              </div>
            </>
          )}
        </div>
        {bruto > 0 && (
          <p style={{ margin: "10px 0 0", fontSize: 12.5, color: C.muted }}>
            Total con IVA (mide el límite de efectivo): <b style={{ color: C.ink }}>{dinero(bruto)}</b>
          </p>
        )}
        {!esEspecie && (
          <>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13.5, marginTop: 10 }}>
              <input type="checkbox" checked={preventa} onChange={(e) => { setPreventa(e.target.checked); }} />
              Es preventa / enganche de un inmueble
            </label>
            {preventa && (
              <div style={{ marginTop: 8 }}>
                <label style={st.label}>Valor total pactado del inmueble</label>
                <input style={st.input} inputMode="numeric" placeholder="0" value={valorInmueble} onChange={(e) => { setValorInmueble(e.target.value.replace(/[^0-9.]/g, "")); }} />
              </div>
            )}
          </>
        )}
      </div>

      {ES_FINANCIAMIENTO.has(tipo) && (
        <div style={st.tarjeta}>
          <p style={st.eyebrow}>Detalle del financiamiento (lo pide el aviso)</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {tipo !== "financiamiento_bursatil" && (
              <div>
                <label style={st.label}>Plazo (meses)</label>
                <input style={st.input} inputMode="numeric" value={finCampo("plazo")} onChange={(e) => { setFinCampo("plazo", e.target.value.replace(/[^0-9]/g, "")); }} />
              </div>
            )}
            {tipo === "prestamo_financiero" && (
              <>
                <div>
                  <label style={st.label}>Tipo de institución (clave)</label>
                  <input style={st.input} inputMode="numeric" value={finCampo("tipo_institucion")} onChange={(e) => { setFinCampo("tipo_institucion", e.target.value.replace(/[^0-9]/g, "")); }} />
                </div>
                <div>
                  <label style={st.label}>Tipo de crédito (clave)</label>
                  <input style={st.input} inputMode="numeric" value={finCampo("tipo_credito")} onChange={(e) => { setFinCampo("tipo_credito", e.target.value.replace(/[^0-9]/g, "")); }} />
                </div>
              </>
            )}
            {tipo === "financiamiento_bursatil" && (
              <>
                <div>
                  <label style={st.label}>Monto solicitado en la emisión</label>
                  <input style={st.input} inputMode="numeric" value={finCampo("monto_solicitado")} onChange={(e) => { setFinCampo("monto_solicitado", e.target.value.replace(/[^0-9.]/g, "")); }} />
                </div>
                <div>
                  <label style={st.label}>Fecha de emisión</label>
                  <input style={st.input} type="date" value={finCampo("fecha_emision")} onChange={(e) => { setFinCampo("fecha_emision", e.target.value); }} />
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <button
        style={{ ...st.btnPrim, width: "100%", opacity: guardando || !listo ? OPACIDAD_DESHABILITADO : 1 }}
        disabled={guardando || !listo}
        onClick={() => {
          setGuardando(true);
          void onCrear(armar()).finally(() => { setGuardando(false); });
        }}
      >
        {guardando ? "Registrando…" : demo ? "Registrar (requiere backend)" : "Registrar — el motor evalúa al guardar"}
      </button>
      <p style={{ fontSize: 12, color: C.muted, textAlign: "center", margin: "10px 0 24px" }}>
        Al guardar verás el semáforo y el cálculo normativo completo de esta operación.
      </p>
    </div>
  );
}
